// src/components/map/hooks/adapters/useMapNavigation.ts
//
// Adapter hook: Navigation
//
// Owns:
//   - fitBounds() callback (extent → mapbox.fitBounds)
//   - flyToSpecificPoint effect (store → mapbox.flyTo)
//   - flyToGroup effect (store → fetchGroupBounds (backend) → mapbox.fitBounds)
//   - flyToExtent effect (store → mapbox.fitBounds, once per command per map
//     instance, claims the debounce window so a colliding filter zoom is
//     skipped; re-runs on the isMapInitialized readiness signal so an extent
//     queued before the map loaded is still consumed, and ignores commands
//     queued before this map instance mounted)
//   - Well auto-zoom effect (wellListFilterExtent change → fitBounds, gated by
//     autoZoomSetting OR zoomToFilterSetting)
//   - Well list single-well open (wellListOpenStore → a flyToExtent at zoom 13
//     and the well card, or the card alone when the map does not zoom)
//   - Facility auto-zoom effect (facilityListFilterExtent change → fitBounds,
//     gated by autoZoomSetting OR zoomToFilterSetting)
//   - Combined midstream auto-zoom effect (both extents → combineExtents → fitBounds)
//
// Mapbox methods called: fitBounds, flyTo
//
// Store reads: selectFlyToGroup, selectFlyToSpecificPoint, selectFlyToExtent,
//              selectTxnIdId, setFlyToGroup
// Redux reads: filter.wellListFilterExtent, facilityFilter.wellListFilterExtent,
//              filter.isMidstreamNavigatorFilter, facilityFilter.isMidstreamNavigatorFilter
//
// Internal state: lastZoomTime ref (debounce guard for rapid sequential zooms),
//                 flyReqId ref (monotonic request id — supersession guard for
//                 the async flyToGroup fetch, see docs/GROUP_ZOOM_TO_FIT.md §4.3),
//                 lastConsumedFlyToExtent ref (newest fly-to-extent this map
//                 instance has already fitted),
//                 mountedAt ref (when this instance mounted — fly-to-extent
//                 commands stamped earlier are not for this map)
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";

import mapboxgl from "mapbox-gl";
import { RootState } from "store/rootReducer";
import {
  selectFlyToExtent,
  selectFlyToGroup,
  selectFlyToSpecificPoint,
  selectTxnIdId,
  useMapStore
} from "stores/map";
import { selectWellListOpen, useWellListOpenStore } from "stores/wellListOpenStore";

import { useUserEvents } from "hooks/useUserEvents";

import { fetchGroupBounds } from "api/map";

import { WELL_CARD_VIEW_SOURCE } from "components/well-card-panel/constants";
import { padClearOfWellCard } from "components/well-card-panel/utils/wellCardSpan";

import { EntityKind } from "../../../../models/entityKind";
import { EntitiesInGroupModel } from "../../contexts/MapContextState";
import { isPanToNetworkBoundsActive } from "../../toolbar/midstream-navigator/hooks/mapState";
import { Extent, combineExtents } from "../../utils/combineExtents";
import {
  SINGLE_WELL_MAX_ZOOM,
  isLatestWellOpen,
  isMapOnScreen,
  selectAndOpenWell,
  wellMidpoint
} from "../../utils/openSingleWell";

// ── Constants ────────────────────────────────────────────────────────────────

/** Minimum interval (ms) between sequential auto-zoom operations. */
const ZOOM_DEBOUNCE_MS = 100;

/** Default zoom cap for auto-zoom / filter-extent fits. */
const DEFAULT_MAX_ZOOM = 12;

/**
 * Zoom cap when fitting to a double-clicked group. Higher than the default so
 * fitting a small, tight cluster of wells zooms in close instead of pulling
 * back to the default cap.
 */
const GROUP_MAX_ZOOM = 14;

/** Breathing room around a fitted extent, in pixels. */
const DEFAULT_FIT_PADDING = 20;

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseMapNavigationParams {
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  readonly entitiesInGroupRef: MutableRefObject<Record<string, EntitiesInGroupModel>>;
  readonly activeEntitiesRef: MutableRefObject<EntityKind[]>;
  readonly autoZoomSetting: Maybe<{ isAutoZoomEnabled: boolean }>;
  readonly zoomToFilterSetting: Maybe<{ isZoomToFilterEnabled: boolean }>;
  /**
   * Map-readiness signal from the map context — the same one
   * useMapTileSourceManager and useRigFracLayerFilter key their own
   * re-application on. Reactive, unlike `mapboxRef`, so the flyToExtent effect
   * can re-run once a map exists.
   */
  readonly isMapInitialized?: boolean;
}

export interface UseMapNavigationReturn {
  /** Fit the map viewport to the given extent with padding and maxZoom. */
  readonly fitBounds: (extent: Maybe<Extent>, maxZoom?: number, padding?: number) => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapNavigation({
  mapboxRef,
  activeEntitiesRef,
  autoZoomSetting,
  zoomToFilterSetting,
  isMapInitialized
}: UseMapNavigationParams): UseMapNavigationReturn {
  // ── Store / Redux selectors ────────────────────────────────────────────
  const flyToGroup = useMapStore(selectFlyToGroup);
  const setFlyToGroup = useMapStore((s) => s.setFlyToGroup);
  const flyToSpecificPoint = useMapStore(selectFlyToSpecificPoint);
  const flyToExtent = useMapStore(selectFlyToExtent);
  const txnIdId = useMapStore(selectTxnIdId);
  const queryClient = useQueryClient();
  const setFlyToExtent = useMapStore((s) => s.setFlyToExtent);
  const wellListOpen = useWellListOpenStore(selectWellListOpen);
  const dispatch = useDispatch();
  const { sendWellCardViewedEvent } = useUserEvents();

  const wellListFilterExtent = useSelector(
    (state: RootState) => state.filter.wellListFilterExtent
  );
  const facilityListFilterExtent = useSelector(
    (state: RootState) => state.facilityFilter.wellListFilterExtent
  );
  const isMidstreamNavigatorFilter = useSelector(
    (state: RootState) => state.filter.isMidstreamNavigatorFilter
  );
  const isMidstreamNavigatorFacilityFilter = useSelector(
    (state: RootState) => state.facilityFilter.isMidstreamNavigatorFilter
  );

  // ── Internal state ─────────────────────────────────────────────────────
  const lastZoomTime = useRef(0);
  // Monotonic request id — supersession token for the async flyToGroup fetch.
  // Deliberately NOT a cleanup `ignore` flag: this effect's own reset
  // re-render (setFlyToGroup(undefined)) would flip a cleanup flag and
  // poison the in-flight fetch on the normal path. See docs/GROUP_ZOOM_TO_FIT.md §4.3.
  const flyReqId = useRef(0);
  // Timestamp of the newest fly-to-extent command this map has already fitted.
  // Per instance, so a second map widget on the same dashboard consumes the
  // same command independently instead of racing the first one for it.
  const lastConsumedFlyToExtent = useRef(0);
  // When this map instance mounted. A fly-to-extent stamped before then was a
  // pick made with no map in the layout (or aimed at an earlier instance), so
  // it is skipped no matter how much later this map finishes loading — a pick
  // made while this map was still loading stays consumable indefinitely.
  const mountedAt = useRef(Date.now());

  // ── fitBounds callback ─────────────────────────────────────────────────
  // Note: `mapboxRef` in the dependency array does not change.
  const fitBounds = useCallback(
    (
      extent: Maybe<Extent>,
      maxZoom: number = DEFAULT_MAX_ZOOM,
      padding: number | mapboxgl.PaddingOptions = DEFAULT_FIT_PADDING
    ) => {
      if (!extent || !mapboxRef.current) {
        return;
      }
      const mapbox = mapboxRef.current;
      // cameraForBounds derives scale from the container minus padding on each
      // side, so padding wider than the container gives a negative scale and a
      // NaN zoom — silently, with no throw. A map widget can be resized small
      // enough to reach that, so the padding yields rather than the camera
      // breaking.
      const canvas = mapbox.getCanvas?.();
      const shortestSide = Math.min(canvas?.clientWidth ?? 0, canvas?.clientHeight ?? 0);
      // Only clamp when the container can actually be measured; an unmeasurable
      // one keeps the requested padding rather than silently collapsing to 0.
      // Per-side padding is passed through — the caller that asks for it is
      // compensating for a specific obstruction and knows its own numbers.
      const safePadding =
        typeof padding === "number" && shortestSide > 0
          ? Math.max(0, Math.min(padding, Math.floor(shortestSide / 2) - 1))
          : padding;
      mapbox.fitBounds(
        [
          [extent.minx, extent.miny],
          [extent.maxx, extent.maxy]
        ],
        { padding: safePadding, maxZoom }
      );
    },
    [mapboxRef]
  );

  // ── flyToSpecificPoint effect ──────────────────────────────────────────
  useEffect(() => {
    // Guard against the initial/reset store value (`undefined`) — in
    // production `mapboxRef.current` is null until the map finishes loading,
    // so this effect is a no-op on the very first mount; the explicit
    // `flyToSpecificPoint` check makes that invariant defensive rather than
    // implicit.
    if (!mapboxRef.current || !flyToSpecificPoint) {
      return;
    }
    const mapbox = mapboxRef.current;
    mapbox.flyTo({
      center: [flyToSpecificPoint.lng, flyToSpecificPoint.lat],
      zoom: flyToSpecificPoint.zoom,
      essential: true
    });
  }, [flyToSpecificPoint]);

  // ── flyToGroup effect ──────────────────────────────────────────────────
  // Backend-driven bounds (docs/GROUP_ZOOM_TO_FIT.md §3.2/§4.3): no longer
  // depends on `entitiesInGroup` or currently-loaded MVT tiles, so it works
  // identically in raster mode / zoomed all the way out.
  useEffect(() => {
    // Reset command lands here BEFORE bumping flyReqId — this must run
    // before the early returns so the reset re-render caused by
    // setFlyToGroup(undefined) below doesn't bump the counter itself.
    if (!flyToGroup) {
      return;
    }
    const title = flyToGroup;
    const txn = txnIdId;
    // Consume the trigger up front so the same group can be double-clicked
    // again. Safe here (unlike a cleanup-driven flag) because this re-render
    // hits the `!flyToGroup` guard above before ++flyReqId ever runs again.
    setFlyToGroup(undefined);

    if (!txn) {
      return;
    }

    const reqId = ++flyReqId.current;
    (async () => {
      const bounds = await fetchGroupBounds(queryClient, txn, title);
      // Discard if superseded by either:
      //  - a newer double-click (flyReqId bumped past reqId), or
      //  - a txn change while the fetch was in flight (filter change).
      const txnChanged = selectTxnIdId(useMapStore.getState()) !== txn;
      if (reqId !== flyReqId.current || txnChanged || !bounds) {
        return;
      }
      // Reuse the shared fitBounds callback so padding stays in one place,
      // but allow a closer zoom cap for a double-clicked group (a tight
      // cluster shouldn't pull back to the default cap).
      fitBounds(
        {
          minx: bounds.sw[0],
          miny: bounds.sw[1],
          maxx: bounds.ne[0],
          maxy: bounds.ne[1]
        },
        GROUP_MAX_ZOOM
      );
    })();
    // No cleanup: fitBounds already guards on mapboxRef.current, so a late
    // resolve after unmount is a harmless no-op (no React state is set
    // post-await).
  }, [flyToGroup, txnIdId, setFlyToGroup, fitBounds, queryClient]);

  // ── flyToExtent effect ─────────────────────────────────────────────────
  // Placed ahead of the auto-zoom effects on purpose: it claims the debounce
  // window, so a filter zoom landing within ZOOM_DEBOUNCE_MS of this one is
  // skipped. Anything later still zooms — this is not a general suppression.
  // Keyed on `isMapInitialized` as well as the command: bailing on
  // `mapboxRef.current` alone would strand the extent forever, because a ref is
  // not reactive and the command does not change again while it waits.
  //
  // The command is deliberately left in the store. Consumption is tracked per
  // map instance instead: every map fits every command newer than the last one
  // it consumed, so a dashboard with two map widgets fits both rather than the
  // first one clearing the command out from under the second. A command older
  // than the staleness window is ignored outright, which is what keeps a pick
  // made in a mapless layout from firing whenever a map next appears —
  // including on a remount, where nothing has been consumed yet.
  useEffect(() => {
    // Same shape as the flyToSpecificPoint effect: bail when there is no map
    // to fit, so the extent survives until a map exists.
    if (!flyToExtent || !mapboxRef.current) {
      return;
    }
    const { extent, at } = flyToExtent;
    if (at <= lastConsumedFlyToExtent.current) {
      return;
    }
    // Only commands issued while this map was already mounted are for it —
    // deliberately not a wall-clock cutoff, so a slow cold load can never
    // swallow a pick the user made while waiting for it.
    if (at < mountedAt.current) {
      return;
    }
    lastConsumedFlyToExtent.current = at;
    // Fitting to a single shapefile uses the same cap as a group zoom so a
    // small polygon fills the viewport instead of stopping at the wider
    // filter-zoom cap.
    // Framing comes from the command, so a producer that wants different
    // framing says so without changing it for the other one. Shapefile-category
    // double-click sends none and keeps the group cap and default padding it
    // has always had.
    const padding = flyToExtent.options?.padding ?? DEFAULT_FIT_PADDING;
    fitBounds(
      extent,
      flyToExtent.options?.maxZoom ?? GROUP_MAX_ZOOM,
      // Per map: each one sits in a different place under the card.
      flyToExtent.options?.clearWellCard
        ? padClearOfWellCard(
            padding,
            mapboxRef.current.getContainer?.().getBoundingClientRect()
          )
        : padding
    );
    lastZoomTime.current = Date.now();
  }, [flyToExtent, isMapInitialized, fitBounds, mapboxRef]);

  // Individual well zoom - only when:
  // 1. Auto-zoom or zoom-to-filter is enabled
  // 2. Wells are active
  // 3. Not in full midstream mode
  // 4. Not during an active panToNetworkBounds operation
  const zoomsToWells = () =>
    (autoZoomSetting?.isAutoZoomEnabled || zoomToFilterSetting?.isZoomToFilterEnabled) &&
    activeEntitiesRef.current.includes(EntityKind.Well) &&
    !(isMidstreamNavigatorFilter && isMidstreamNavigatorFacilityFilter) &&
    !isPanToNetworkBoundsActive;

  // ── Well list single-well open ─────────────────────────────────────────
  // One map claims the open. The camera goes through the fly-to-extent command,
  // so every map moves, as it did for the bbox.
  useEffect(() => {
    const open = wellListOpen;
    if (!open || open.at < mountedAt.current || !isLatestWellOpen(open.token)) return;
    if (!useWellListOpenStore.getState().claim(open)) return;
    const { uwi, extent, token } = open;
    selectAndOpenWell(uwi, dispatch);
    // Before the zoom check: the card opens whether or not this map zooms.
    sendWellCardViewedEvent({
      uwi,
      entityKind: EntityKind.Well,
      source: WELL_CARD_VIEW_SOURCE.wellListInput
    });
    if (!zoomsToWells()) return;
    // Set before the midpoint loads, so no map fits the bbox in the meantime.
    useWellListOpenStore.getState().setFlying(open);
    // A map out of sight opens the card only. The mark above keeps the bbox fit
    // off its camera as well.
    if (mapboxRef.current && !isMapOnScreen(mapboxRef.current)) return;
    (async () => {
      const midpoint = await wellMidpoint(uwi);
      // A later jump or user filter replaced it while the midpoint loaded.
      if (!isLatestWellOpen(token) || useWellListOpenStore.getState().pending !== open) {
        return;
      }
      const target = midpoint
        ? { minx: midpoint[0], miny: midpoint[1], maxx: midpoint[0], maxy: midpoint[1] }
        : extent;
      setFlyToExtent(target, { maxZoom: SINGLE_WELL_MAX_ZOOM, clearWellCard: true });
    })();
  }, [wellListOpen]);

  // ── Well auto-zoom effect ──────────────────────────────────────────────
  useEffect(() => {
    const { pending, flying } = useWellListOpenStore.getState();
    if (
      wellListFilterExtent &&
      zoomsToWells() &&
      // The single-well open above makes the one camera move for this extent,
      // but only when the claiming map zooms at all.
      !(pending && flying === pending && pending.extent === wellListFilterExtent)
    ) {
      const now = Date.now();
      if (now - lastZoomTime.current > ZOOM_DEBOUNCE_MS) {
        lastZoomTime.current = now;
        fitBounds(wellListFilterExtent);
      }
    }
  }, [
    wellListFilterExtent,
    isMidstreamNavigatorFilter,
    isMidstreamNavigatorFacilityFilter
  ]);

  // ── Facility auto-zoom effect ──────────────────────────────────────────
  useEffect(() => {
    // Individual facility zoom effect - only fire when:
    // 1. Auto-zoom or zoom-to-filter is enabled
    // 2. Facilities are active
    // 3. Not in full midstream mode
    // 4. Not during an active panToNetworkBounds operation
    if (
      (autoZoomSetting?.isAutoZoomEnabled ||
        zoomToFilterSetting?.isZoomToFilterEnabled) &&
      facilityListFilterExtent &&
      activeEntitiesRef.current.includes(EntityKind.Facility) &&
      !(isMidstreamNavigatorFilter && isMidstreamNavigatorFacilityFilter) &&
      !isPanToNetworkBoundsActive // Check if panToNetworkBounds is active
    ) {
      const now = Date.now();
      if (now - lastZoomTime.current > ZOOM_DEBOUNCE_MS) {
        lastZoomTime.current = now;
        fitBounds(facilityListFilterExtent);
      }
    }
  }, [
    facilityListFilterExtent,
    isMidstreamNavigatorFilter,
    isMidstreamNavigatorFacilityFilter
  ]);

  // ── Combined midstream auto-zoom effect ────────────────────────────────
  useEffect(() => {
    // Only combine when:
    // 1. Auto-zoom is enabled
    // 2. BOTH flags are true (explicitly in midstream mode)
    // 3. Not during an active panToNetworkBounds operation
    if (
      !autoZoomSetting?.isAutoZoomEnabled ||
      !isMidstreamNavigatorFilter ||
      !isMidstreamNavigatorFacilityFilter ||
      (!wellListFilterExtent && !facilityListFilterExtent) ||
      isPanToNetworkBoundsActive // Don't zoom if panToNetworkBounds is active
    ) {
      return;
    }

    // Use utility function to combine extents when in midstream navigator mode
    const combinedExtent = combineExtents(wellListFilterExtent, facilityListFilterExtent);

    if (combinedExtent) {
      fitBounds(combinedExtent);
    }
  }, [
    wellListFilterExtent,
    facilityListFilterExtent,
    isMidstreamNavigatorFilter,
    isMidstreamNavigatorFacilityFilter,
    fitBounds
  ]);

  return { fitBounds };
}
